import React from 'react';
import { useGetEnrollmentHistoryQuery } from '../../store/api/localApi';
import { dublinDateString, formatCivilDate } from '../../utils/date';
import { formatWeekdays } from '../../utils/terms';
import type {
  EnrollmentEndReason,
  EnrollmentHistoryPeriod,
  EnrollmentHistoryTerm,
} from '../../types';

interface Props {
  studentId: string;
}

const END_REASON_LABEL: Record<EnrollmentEndReason, string> = {
  LEFT_EARLY: 'removed (left early)',
  CHILD_LEFT_SERVICE: 'left the service',
  TRANSFERRED: 'transferred',
  CANCELLED_BEFORE_START: 'cancelled before start',
};

/** "3 Nov 10:12" — an instant on the Dublin calendar and clock. */
const formatDayMonthTime = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-IE', {
    timeZone: 'Europe/Dublin',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('day')} ${part('month')} ${part('hour')}:${part('minute')}`;
};

const withWhen = (text: string, iso: string | undefined): string => {
  const when = formatDayMonthTime(iso);
  return when ? `${text}, ${when}` : text;
};

/**
 * The dates a period covers. An open period runs "today" while its term does;
 * one in a term that has ended ran to the term's last day, and one that has
 * not begun yet is shown from its start.
 */
const periodDates = (
  period: EnrollmentHistoryPeriod,
  term: EnrollmentHistoryTerm,
  today: string
): string => {
  if (isCancelledBooking(period)) return 'booking cancelled';
  const from = formatCivilDate(period.from);
  if (period.to) return `${from} – ${formatCivilDate(period.to)}`;
  if (period.from > today) return `from ${from}`;
  if (term.termEndDate < today) return `${from} – ${formatCivilDate(term.termEndDate)}`;
  return `${from} – today`;
};

/** A crossed period (`to` before `from`) never covered a day: the booking was cancelled. */
const isCancelledBooking = (period: EnrollmentHistoryPeriod): boolean =>
  !!period.to && period.to < period.from;

/** "Autumn 2026 term", but never "Autumn Term term". */
const termHeading = (termName: string): string =>
  /\bterm$/i.test(termName.trim()) ? termName : `${termName} term`;

/** Who set the period, or why no one is named. */
const setByLine = (period: EnrollmentHistoryPeriod): string | null => {
  if (period.setBy) return withWhen(`set by ${period.setBy.name}`, period.setAt);
  if (period.implied) return 'before change history was kept';
  if (period.setAt) return 'rolled over';
  return null;
};

const endLine = (period: EnrollmentHistoryPeriod): string | null => {
  if (isCancelledBooking(period)) {
    return period.endedBy ? withWhen(`by ${period.endedBy.name}`, period.endedAt) : null;
  }
  if (!period.endReason) return null;
  const label = END_REASON_LABEL[period.endReason] ?? period.endReason.toLowerCase();
  const by = period.endedBy ? `${label} by ${period.endedBy.name}` : label;
  return withWhen(by, period.endedAt);
};

/**
 * The child profile's "Enrolment history": every period of every class the
 * child has had at this location, newest first, with who set each one and
 * why it ended. Staff only — StudentDetail renders it outside the parent view,
 * and the read refuses parents.
 */
const EnrollmentHistorySection: React.FC<Props> = ({ studentId }) => {
  const { data, isLoading, isError } = useGetEnrollmentHistoryQuery(studentId);
  const today = dublinDateString();
  const classes = data?.classes ?? [];

  return (
    <section data-testid="enrollment-history-section" className="mt-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">Enrolment history</h3>
      {isLoading ? (
        <p data-testid="enrollment-history-loading" className="text-xs text-gray-500">
          Loading…
        </p>
      ) : isError ? (
        <p data-testid="enrollment-history-error" className="text-xs text-red-600">
          Couldn&apos;t load enrolment history.
        </p>
      ) : classes.length === 0 ? (
        <p data-testid="enrollment-history-empty" className="text-xs text-gray-500">
          No enrolment history yet.
        </p>
      ) : (
        <div className="space-y-3">
          {classes.map((cls) => (
            <div
              key={cls.classId}
              data-testid={`enrollment-history-class-${cls.classId}`}
              className="border border-gray-100 rounded"
            >
              {[...cls.terms]
                .sort((a, b) => b.termStartDate.localeCompare(a.termStartDate))
                .map((term) => (
                  <div key={term.termId} className="px-3 py-2">
                    <h4 className="text-sm font-medium text-gray-900">
                      {cls.className} — {termHeading(term.termName)}
                    </h4>
                    <ul className="mt-1 divide-y divide-gray-100">
                      {[...term.periods]
                        .sort((a, b) => b.from.localeCompare(a.from))
                        .map((period) => {
                          // A cancelled booking reads as one line: what
                          // happened and who did it.
                          const setBy = isCancelledBooking(period) ? null : setByLine(period);
                          const ended = endLine(period);
                          return (
                            <li
                              key={period.from}
                              data-testid={`enrollment-history-period-${cls.classId}-${term.termId}-${period.from}`}
                              className="py-1.5 text-xs text-gray-700 sm:flex sm:gap-4"
                            >
                              <span className="sm:w-48 shrink-0 font-medium text-gray-900">
                                {periodDates(period, term, today)}
                              </span>
                              <span className="sm:w-36 shrink-0">
                                {formatWeekdays(period.daysOfWeek)}
                              </span>
                              <span className="text-gray-500">
                                {setBy && <span className="block">{setBy}</span>}
                                {ended && <span className="block">{ended}</span>}
                              </span>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default EnrollmentHistorySection;
